import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { CreateArtifactContext } from "../../../../context/CreateArtifactContext";
import {
  ContentState,
  ContentSubstate,
  SKArtifact,
  SKGreetingArtifact,
  TopicTypeString,
  getArtefactIcon,
  getContentCreatorActionableItems,
} from "../../../../models/SKArtifact";
import { useNavigationBar } from "../../../../context/NavigationContext";

import { capitalizeFirstLetter } from "../../../../utils/stringFunctions";
import { AddContentItem } from "./components/AddContentItem";

import { AddTextField } from "../../../../components/AddTextField";
import { AddTextAreaField } from "../../../../components/AddTextAreaField";
import SelectGreetingType from "../SelectGreetingType";

import ContentHistoryNavigator from "./components/ContentHistoryNavigator";
import ContextCheckListCard from "./components/ContextCheckListCard";
import { PublishingTier } from "./components/PublishingTier";
import { ArtifactContainerRef } from "./CreateArtifact";

import ContentEditNavigator from "../../../contentModeration/ContentEditNavigator";
import { ContentAgeRestriction } from "../../../../api/enums";
import ContentReportNavigator from "./components/ContentReportNavigator";
import { useAuth, UserRole } from "../../../../context/AuthContext";
const GreetingsArtifactContainer = forwardRef<ArtifactContainerRef>((props, ref) => {
  const { contentData, updateContentData, artifactId, submit, isModerator, isContentEditing, isEditable } = useContext(CreateArtifactContext);
  const [isValidForm, setIsValidForm] = useState(false);
  const context = useAuth();
  const [openTypeSeletion, setOpenTypeSeletion] = useState(false);
  const [greetingData, setGreetingData] = useState<SKGreetingArtifact | undefined>(undefined);
  const navigate = useNavigate();
  const { setNavProps } = useNavigationBar();

  const [error, setErrorState] = useState<{ [key: string]: string | undefined }>({});

  const setErrors = (newErrors: { [key: string]: string | undefined }) => {
    setErrorState(newErrors);
  };

  useImperativeHandle(ref, () => ({
    containerSchema: () => schema,
    setErrors: newErrors => setErrors(newErrors),
  }));

  const schema = Yup.object().shape({
    type: Yup.string().required(),
    title: Yup.string().required().max(50),
    subtitle: Yup.string().notRequired().max(500),
    text: Yup.string().notRequired().max(500),
    topic: Yup.string().required(),
    tier: Yup.string().required(),
    age: Yup.number().required(),
    status: Yup.string().required(),
    certifyFirst: Yup.boolean().oneOf([true]).required("Please certify content"),
    certifySecond: Yup.boolean().oneOf([true]).required("Please certify content"),
    // autoPublish: Yup.boolean().required(),
    greetingType: Yup.string().required(),
    greetingTitle: Yup.string().required(),
    greetingMessage: Yup.string().required(),
  });

  useEffect(() => {
    const layout = (contentData as SKGreetingArtifact).greetingType;
    if (layout) {
      setGreetingData(contentData as SKGreetingArtifact);
    }
  }, [contentData]);

  const handleAddContentItemClick = useCallback(() => {
    const greetingType = greetingData?.greetingType;
    if (greetingType) {
      navigate("addGreeting");
    } else {
      setOpenTypeSeletion(true);
    }
  }, [greetingData?.greetingType, navigate]);

  return (
    <>
      <div className="artifact-page-context">
        {isModerator &&
        !isContentEditing &&
        artifactId &&
        contentData.status === ContentState.ACTIVE &&
        contentData.subStatus === ContentSubstate.EDIT_IN_REVIEW ? (
          <ContentEditNavigator title="Channel Edit" docId={artifactId!} />
        ) : (
          ""
        )}

        {artifactId && (contentData as SKArtifact).hasHistory ? <ContentHistoryNavigator title="Channel Status" contentId={artifactId} /> : ""}
        {artifactId && context?.userRoles?.includes(UserRole.SkarvoAdmin) && contentData.reportCount && contentData.reportCount > 0 ? (
          <ContentReportNavigator title="Content Reports" />
        ) : (
          ""
        )}
        <AddContentItem index={1} onClick={handleAddContentItemClick} count={(contentData as SKGreetingArtifact)?.greetingType ? 1 : 0} />

        <SelectGreetingType
          isOpen={openTypeSeletion}
          onClose={() => {
            setOpenTypeSeletion(false);
            // setTimeout(handleAddContentItemClick, 0);
          }}
          onSelect={() => {
            handleAddContentItemClick();
          }}
        />

        {/* <div className="flex flex-col gap-[30px] mt-[10px]">
        {artifactId && (contentData as SKArtifact).hasHistory && (
          <ContentStatusView />
        )}
        {isButtonClicked && (
          <SelectGreetingType
            isOpen={isButtonClicked}
            onClose={() => {
              setIsButtonClicked(false);
              handleAddContentItemClick();
            }}
          />
        )}


        <AddContentItem
          index={1}
          onClick={handleAddContentItemClick}
          count={(contentData as SKGreetingArtifact)?.greetingType ? 1 : 0}
        /> */}

        <AddTextField
          name={"addtitle"}
          title={"Add Title"}
          note={"Title can be the subject, purpose, theme or topic of your post."}
          placeholder={"Enter Title"}
          index={2}
          charAllowed={100}
          value={(contentData as SKArtifact).title}
          onChange={async e => {
            updateContentData({ title: e.target.value! });
          }}
          disabled={!isEditable}
          error={error?.title}
        />

        <AddTextAreaField
          name={"addtext"}
          title={"Add Text"}
          note={"Maximum text length of 500 characters."}
          placeholder={"Enter Text"}
          index={3}
          height={150}
          charAllowed={500}
          value={(contentData as SKArtifact).subtitle as string}
          onChange={async e => {
            updateContentData({ subtitle: e.target.value! });
          }}
          disabled={!isEditable}
        />
        <ContextCheckListCard
          index={4}
          name="Add Topic"
          title="Choose the primary topic of your Content."
          purposeText="Add Topic"
          notes="Choose the topic that represents the primary focus of most of your Content."
          doneText={contentData.topic ? capitalizeFirstLetter(TopicTypeString[contentData.topic]) : undefined}
          action={() => {
            navigate("selectTopic");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={5}
          name="Add Age Rating"
          title="Choose the Age Rating of your Content"
          purposeText="Add Age Rating"
          notes="Choose the age rating based on the appropriateness of your Content for children."
          doneText={contentData.age ? "Age " + contentData.age + "+" : undefined}
          action={() => {
            navigate("ageRating");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={6}
          name="Certify Content"
          title="Certify Content"
          purposeText="Certify Content"
          notes="Certify that your Content would ensure and comply with these requirements."
          doneText={contentData.certifyFirst && contentData.certifySecond ? "Certified" : undefined}
          action={() => {
            navigate("certifyContent");
          }}
          disabled={!isEditable}
        />
        <PublishingTier index={7} />
      </div>
    </>
  );
});

export default GreetingsArtifactContainer;
